import React, { useMemo } from 'react';
import first from 'lodash.first';

import useMedia from 'hooks/useMedia';

import TableLoader from 'components/utility/ContentLoader/TableLoader/TableLoader';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper/index';
import Table from 'components/data-visualization/Table/Table';
import PlaylistsTableRow from './TableRow';

import {
  TableHeader,
  HorizontalScrollContainer,
  Thead,
} from 'components/Overview/BreakdownTable/styles';
import BreakdownTablePlaceholder from 'components/data-visualization/Table/BreakdownTablePlaceholder';
import Layout from 'components/styled/Layout/Layout';
import { PromiseState } from 'react-refetch';
import { IRange } from 'components/data-containers/types';
import { IPlaylist } from './types';

interface IPlaylistData {
  entityData: {
    entity: {
      id: string;
      type: string;
    };
    playlist_count: number;
    playlists: IPlaylist[];
  }[];
  valueRanges: {
    [key: string]: IRange;
    followersRange: IRange;
    skipRateThisWeekToDateRange: IRange;
    streamCountThisWeekToDateRange: IRange;
    streamWowInProgressRange: IRange;
    trackCountRange: IRange;
  };
}

interface IPlaylistsTable {
  headers: string[];
  playlistData: PromiseState<IPlaylistData>;
  isPending: boolean;
}

const PlaylistsTable: React.FC<IPlaylistsTable> = (props) => {
  const { headers, playlistData, isPending } = props;

  const isLargeScreen = useMedia('(min-width: 50em)');

  const playlists = useMemo(() => {
    if (!playlistData.value) return;

    return first<any>(playlistData.value.entityData).playlists;
  }, [playlistData]);

  /**
   * True if there are no breakdowns to display.
   * @returns {boolean}
   */
  const hasNoData = useMemo(() => {
    if (Array.isArray(playlists)) {
      return playlists.length === 0;
    }
    return !isPending;
  }, [playlists, isPending]);

  const headerCount = useMemo(() => {
    return headers.filter((_: any, idx: number) => isLargeScreen || idx < 2)
      .length;
  }, [headers, isLargeScreen]);

  return (
    <HorizontalScrollContainer>
      <AnimatedLoadingWrapper loading={isPending}>
        <Layout.Relative>
          <Table>
            <Thead>
              <tr>
                {headers
                  .filter((_: any, idx: number) => isLargeScreen || idx < 2)
                  .map((header: string, index: number) =>
                    header === 'streams week over week' ? (
                      <TableHeader key={header} wowRow fixedWidth="100%">
                        <p style={{ margin: 0 }}>
                          streams <br />
                          week <br />
                          over week
                        </p>
                      </TableHeader>
                    ) : (
                      <TableHeader
                        alignRight={index !== 0}
                        key={header}
                        fixedWidth={index !== 0 ? '11.5%' : '19.5%'}
                      >
                        {header}
                      </TableHeader>
                    )
                  )}
              </tr>
            </Thead>
            <tbody>
              {isPending && <TableLoader columnCount={headerCount} />}
              {hasNoData && !isPending && (
                <BreakdownTablePlaceholder
                  isSmallScreen={!isLargeScreen}
                  columnCount={headerCount}
                />
              )}
              {playlists &&
                playlists.map(
                  (playlist: IPlaylist): JSX.Element => (
                    <PlaylistsTableRow
                      key={playlist.displayName}
                      isLargeScreen={isLargeScreen}
                      playlist={playlist}
                    />
                  )
                )}
            </tbody>
          </Table>
        </Layout.Relative>
      </AnimatedLoadingWrapper>
    </HorizontalScrollContainer>
  );
};

export default PlaylistsTable;
